// aiConfig.js — central AI configuration for Hayah.
//
// Owns: per-feature model assignments, fallback chains, and the prompt
// version constant. Every AI surface in Hayah imports MODELS[<feature>]
// instead of letting individual call sites pick a model.
//
// Rollback procedure (see HAYAH.md for full steps):
//   1. Set PROMPT_VERSION below back to "v1".
//   2. Swap call sites that import from this file to import PROMPTS_V1
//      from prompts_v1_archive.js instead.
//   3. Revert MODELS to all "gemini-2.5-flash" (the v1 default).

export const PROMPT_VERSION = "v2";

// Per-feature model assignment. Trivial / latency-sensitive paths use
// flash-lite; one-shot reasoning paths use 2.5-pro; vision and search
// stay on flash for cost/quality balance.
//
// Coach surfaces respect the user's Settings model picker (see
// modelForFeature below) so cost-conscious users can dial Pro down.
export const MODELS = {
  // AI Coach — conversational surface. Flash is 4x cheaper than Pro
  // ($0.30/$2.50 vs $1.25/$10 per 1M tokens). User can override via
  // Settings model picker (see modelForFeature below).
  coachChat:        "gemini-2.5-flash",
  coachVision:      "gemini-2.5-flash",
  coachSummary:     "gemini-2.5-flash",

  // Insight prompts — pro for quality (one-shot, cached output).
  coachReport:      "gemini-2.5-pro",
  patternInsights:  "gemini-2.5-pro",
  postWorkout:      "gemini-2.5-flash",
  weeklyRecap:      "gemini-2.5-pro",
  lastWorkoutInsight: "gemini-2.5-flash",
  todayInsights:    "gemini-2.5-flash",

  // Food prompts — flash for structured/visual extraction.
  foodSearch:       "gemini-2.5-flash",
  foodPhoto:        "gemini-2.5-flash",
  mealParser:       "gemini-2.5-flash",
  redescribe:       "gemini-2.5-flash",

  // Riyadah trivial — flash-lite.
  workoutName:      "gemini-2.5-flash-lite",
  workoutGen:       "gemini-2.5-flash-lite",
  workoutRationale: "gemini-2.5-flash-lite",

  // Fast-broken toast — flash-lite (latency budget 7s).
  fastInsight:      "gemini-2.5-flash-lite",

  // Phase 2A — readiness + ketone + treadmill.
  readiness:        "gemini-2.5-pro",       // objective classification, high quality
  ketonePhoto:      "gemini-2.5-flash",     // vision + structured extraction
  homeTreadmill:    "gemini-2.5-flash-lite", // latency-sensitive one-shot plan
  // REST-day mobility / stretching coach. Flash, not Pro — Pro's thinking
  // tokens silently eat most of the output budget on a multi-stretch schema
  // (observed: 4096 cap → ~150 chars of JSON before truncation). Flash has
  // no implicit thinking budget and adheres well to nested schemas.
  mobilityFlow:     "gemini-2.5-flash",

  // Self-test — whatever the user has selected; resolved at call time.
  selfTest:         null,
};

// Fallback chains — walked by callGeminiResilient on retryable errors.
// 2.5-pro routes degrade to flash before older generations.
export const FALLBACK_CHAINS = {
  "gemini-2.5-pro":             ["gemini-2.5-flash", "gemini-2.5-flash-lite"],
  "gemini-2.5-flash":           ["gemini-2.5-flash-lite"],
  "gemini-2.5-flash-lite":      [],
};

// Caller helper: resolves the model to use for a feature.
// Coach surfaces honour the user's Settings override; everything else
// uses the fixed assignment.
export function modelForFeature(feature, userOverride) {
  const fixed = MODELS[feature];
  if (feature && feature.startsWith("coach") && userOverride) return userOverride;
  return fixed || userOverride || "gemini-2.5-flash";
}

// USD pricing per 1M tokens. Approximate as of 2026-05 from Google's
// published rates. The HayahSettings Token Usage dashboard uses these
// to estimate spend. Edit here if Google's rates change — the dashboard
// re-reads on every render so updates land immediately.
//
// Two caveats:
//   1. Cost is computed against the PRIMARY model per feature. If a
//      request degraded via FALLBACK_CHAINS, the real spend was the
//      fallback model's rate (almost always cheaper, so the estimate
//      runs slightly high — fine for budgeting).
//   2. The 2.5 Pro tier above 200K input tokens / 1.5 Flash tier above
//      128K both increase. We don't model the tiering — single-turn
//      Hayah prompts never approach those bounds.
// USD per 1M tokens. `in` = uncached input, `cachedIn` = cached prompt tokens
// (Google bills these at ~25% of normal input), `out` = output AND thinking
// tokens (Google bills 2.5 Pro/Flash reasoning tokens at the output rate).
export const MODEL_PRICING_USD_PER_1M = {
  "gemini-2.5-pro":             { in: 1.25, cachedIn: 0.31,  out: 10.00 },
  "gemini-2.5-flash":           { in: 0.30, cachedIn: 0.075, out: 2.50  },
  "gemini-2.5-flash-lite":      { in: 0.10, cachedIn: 0.025, out: 0.40  },
};

// Returns the USD cost for a given input/output token count against a model.
// inputTokens is TOTAL prompt tokens (uncached + cached); cachedTokens is the
// subset that hit Gemini's implicit cache (billed at cachedIn rate).
// outputTokens should include thinking tokens (Google bills them as output).
// Returns 0 for unknown models so the dashboard degrades gracefully.
export function costUSD(modelId, inputTokens, outputTokens, cachedTokens = 0) {
  const p = MODEL_PRICING_USD_PER_1M[modelId];
  if (!p) return 0;
  const inT  = Math.max(0, Number(inputTokens || 0));
  const outT = Math.max(0, Number(outputTokens || 0));
  const cIn  = Math.max(0, Math.min(Number(cachedTokens || 0), inT));
  const uncached = inT - cIn;
  return (uncached / 1_000_000) * p.in
       + (cIn      / 1_000_000) * (p.cachedIn ?? p.in)
       + (outT     / 1_000_000) * p.out;
}

// Cost helper for a feature. Prefer passing `servedModel` — the model that
// actually serviced the request (fallback chains can degrade from the primary
// MODELS[feature] assignment, and pricing follows the served model).
export function costForFeature(feature, inputTokens, outputTokens, cachedTokens = 0, servedModel = null) {
  const model = servedModel || MODELS[feature];
  if (!model) return 0;
  return costUSD(model, inputTokens, outputTokens, cachedTokens);
}
